(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(
      require('./ai-chat-core.js'),
      require('./ai-chat-state.js'),
      null,
    );
    return;
  }

  if (root) {
    root.MarineMaxAIChatPricingFlow = factory(
      root.MarineMaxAIChatCore,
      root.MarineMaxAIChatState,
      root,
    );
  }
})(typeof window !== 'undefined' ? window : null, function (core, state, root) {
  'use strict';

  if (!core) {
    throw new Error('MarineMax AI Chat core is required by the pricing flow module');
  }
  if (!state) {
    throw new Error('MarineMax AI Chat state is required by the pricing flow module');
  }

  const continueIcon =
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.5 15L12.5 10L7.5 5"/></svg>';
  const verificationMethodMarkup = Object.freeze({
    email:
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg><span>Email</span>',
    text:
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg><span>Text Message</span>',
  });
  const pricingViews = new Set([
    state.views.PRICING_NAME,
    state.views.VERIFICATION_METHOD,
    state.views.EMAIL_INPUT,
    state.views.VERIFICATION_CODE,
  ]);

  function createPricingFlowController(options = {}) {
    const chatRoot = options.root;
    const stateManager = options.stateManager;
    const client = options.client;
    const navigation = options.navigation;
    const sendMessage = options.sendMessage || (() => {});
    const createQuote = options.createQuote;
    const renderQuote = options.renderQuote;
    const sendQuoteEmail = options.sendQuoteEmail;
    const showDesktopForm = options.showDesktopForm || (() => {});
    const isDesktopFormOnly = options.isDesktopFormOnly || (() => false);
    const resizeInput = options.resizeInput || (() => {});
    const defaultInputPlaceholder = String(options.defaultInputPlaceholder || '');
    const stockNumber = String(options.stockNumber || '');
    const documentRef =
      options.document ||
      (chatRoot && chatRoot.ownerDocument) ||
      (root && root.document) ||
      null;
    const logger =
      options.logger ||
      (root && root.console) ||
      (typeof console !== 'undefined' ? console : { error() {}, warn() {} });
    const schedule =
      options.setTimeout ||
      (root && root.setTimeout && root.setTimeout.bind(root)) ||
      globalThis.setTimeout.bind(globalThis);
    const random = options.random || Math.random;

    if (!chatRoot || !stateManager || !navigation || !documentRef) {
      throw new Error('AI Chat pricing flow dependencies are incomplete');
    }
    if (
      !client ||
      ['startPasswordless', 'verifyPasswordless'].some(
        (methodName) => typeof client[methodName] !== 'function',
      )
    ) {
      throw new Error('An InventoryIQ client is required by the pricing flow module');
    }
    if (
      [createQuote, renderQuote, sendQuoteEmail].some(
        (callback) => typeof callback !== 'function',
      )
    ) {
      throw new Error('Quote callbacks are required by the pricing flow module');
    }

    const chatBox = chatRoot.querySelector('.ai-chat__chat');
    const input = chatRoot.querySelector('.ai-chat__input textarea');
    const sendButton = chatRoot.querySelector('.ai-chat__send-btn');
    const viewQuoteButton = chatRoot.querySelector('.ai-chat__view-quote-btn');
    const suggestionContainer = chatRoot.querySelector('.ai-chat__suggest');
    const backButton = chatRoot.querySelector('.ai-chat__back');
    const expertContainer = chatRoot.querySelector('.ai-chat__hubspot');
    const formContainer = chatRoot.querySelector('.ai-chat__hubspot-form');
    const successSplash = chatRoot.querySelector('.ai-chat__hubspot-splash');
    const inputWrapper = chatRoot.querySelector('.ai-chat__input-wrapper');
    const inputField = chatRoot.querySelector('.ai-chat__input');
    const continueButton = chatRoot.querySelector('.ai-chat__continue-btn');
    let authToken = null;
    let fallbackVerificationCode = '';
    let isBound = false;
    let verificationEmail = '';
    let verificationMethod = '';

    function createElement(tagName, className, textContent) {
      const element = documentRef.createElement(tagName);
      if (className) element.className = className;
      if (textContent !== undefined) element.textContent = textContent;
      return element;
    }

    function focusLater(element) {
      schedule(() => element.focus(), 100);
    }

    function setContinueDisabled(disabled) {
      if (!continueButton) return;
      const isDisabled = Boolean(disabled);
      continueButton.disabled = isDisabled;
      continueButton.classList.toggle('disabled', isDisabled);
    }

    function setContinueLabel(label, withIcon = false) {
      if (!continueButton) return;
      if (withIcon) {
        continueButton.innerHTML = `${label} ${continueIcon}`;
      } else {
        continueButton.textContent = label;
      }
    }

    function resetVerificationButton() {
      setContinueLabel('Verify Code', true);
      setContinueDisabled(false);
    }

    function showInvalidInput(element) {
      if (!element) return;
      element.style.borderColor = '#E82012';
      element.focus();
      schedule(() => {
        element.style.borderColor = '';
      }, 2000);
    }

    function restoreChatInput() {
      if (continueButton) {
        continueButton.style.display = 'none';
        continueButton.dataset.step = '';
        continueButton.textContent = 'Continue';
      }
      if (sendButton) sendButton.style.display = '';
      if (inputWrapper) inputWrapper.style.display = '';
      if (inputField) inputField.style.display = '';
      if (input) {
        resizeInput({ reset: true });
        input.placeholder = defaultInputPlaceholder;
      }
    }

    function renderPricingName(options = {}) {
      if (isDesktopFormOnly()) {
        showDesktopForm();
        return false;
      }
      if (options.savePreviousState !== false) navigation.saveState();

      const viewData = options.data || {};
      const restoredName = String(viewData.name || '');
      stateManager.setView(state.views.PRICING_NAME);
      navigation.scrollHeaderToTop();
      if (expertContainer) expertContainer.style.display = 'none';
      if (successSplash) successSplash.style.display = 'none';
      if (formContainer) formContainer.style.display = 'none';

      if (chatBox) {
        chatBox.style.display = 'block';
        chatBox.innerHTML = '';
        chatBox.appendChild(
          createElement(
            'div',
            'ai-message',
            "Let's unlock that price for you! 👇 What's your name?",
          ),
        );

        const inputContainer = createElement(
          'div',
          'ai-chat__pricing-input-container',
        );
        const nameInput = createElement('input', 'ai-chat__pricing-name-input');
        nameInput.type = 'text';
        nameInput.placeholder = 'Enter your full name';
        nameInput.id = `${chatRoot.id}-pricing-name-input`;
        nameInput.setAttribute('aria-label', 'Full name');
        nameInput.setAttribute('autocomplete', 'name');
        nameInput.dataset.aiChatPricingName = '';
        nameInput.value = restoredName;
        inputContainer.appendChild(nameInput);
        chatBox.appendChild(inputContainer);
        chatBox.scrollTop = chatBox.scrollHeight;
        if (options.focus !== false) focusLater(nameInput);
      }

      if (suggestionContainer) suggestionContainer.style.display = 'none';
      if (backButton) backButton.style.display = 'flex';
      navigation.syncHeaderMode();
      if (inputWrapper) {
        if (inputField) inputField.style.display = 'none';
        inputWrapper.style.display = '';
        inputWrapper.style.visibility = 'visible';
      }

      const shouldShowContinue = Boolean(
        viewData.continueVisible && restoredName.trim(),
      );
      if (sendButton) sendButton.style.display = 'none';
      if (viewQuoteButton) {
        viewQuoteButton.style.display = shouldShowContinue ? 'none' : 'flex';
        viewQuoteButton.style.visibility = 'visible';
      }
      if (continueButton) {
        continueButton.style.display = shouldShowContinue ? 'flex' : 'none';
        if (shouldShowContinue) {
          continueButton.dataset.name = restoredName.trim();
          continueButton.dataset.step = 'name';
          setContinueDisabled(false);
        }
      }
      return true;
    }

    function handleViewQuote() {
      if (!viewQuoteButton) return false;

      if (stateManager.currentView === state.views.PRICING_NAME) {
        const pricingInput = chatRoot.querySelector('[data-ai-chat-pricing-name]');
        const name = pricingInput ? pricingInput.value.trim() : '';
        if (!name) {
          if (pricingInput) pricingInput.focus();
          return false;
        }

        viewQuoteButton.style.display = 'none';
        if (continueButton) {
          continueButton.style.display = 'flex';
          continueButton.dataset.name = name;
          continueButton.dataset.step = 'name';
          setContinueDisabled(false);
        }
        return true;
      }

      const name = input ? input.value.trim() : '';
      if (!name) {
        if (input) input.focus();
        return false;
      }
      viewQuoteButton.style.display = 'none';
      if (continueButton) continueButton.style.display = 'flex';
      return true;
    }

    function renderEmailInput(options = {}) {
      if (options.savePreviousState !== false) navigation.saveState();
      const viewData = options.data || {};
      stateManager.setView(state.views.EMAIL_INPUT);
      if (!chatBox) return false;

      chatBox.innerHTML = '';
      chatBox.appendChild(
        createElement('div', 'ai-message', 'Perfect. Please enter your email address:'),
      );
      const inputContainer = createElement(
        'div',
        'ai-chat__pricing-input-container',
      );
      const emailInput = createElement('input', 'ai-chat__pricing-name-input');
      emailInput.type = 'email';
      emailInput.placeholder = 'Enter your email address';
      emailInput.id = `${chatRoot.id}-email-address-input`;
      emailInput.setAttribute('aria-label', 'Email address');
      emailInput.setAttribute('autocomplete', 'email');
      emailInput.dataset.aiChatEmailAddress = '';
      emailInput.value = String(viewData.email || '');
      inputContainer.appendChild(emailInput);
      chatBox.appendChild(inputContainer);
      chatBox.scrollTop = chatBox.scrollHeight;
      if (options.focus !== false) focusLater(emailInput);

      if (continueButton) {
        continueButton.dataset.step = 'email';
        setContinueDisabled(true);
        continueButton.textContent = 'Continue';
      }
      const validateEmailInput = () => {
        setContinueDisabled(!core.isValidEmail(emailInput.value.trim()));
      };
      emailInput.addEventListener('input', validateEmailInput);
      validateEmailInput();
      return true;
    }

    async function startVerification(email) {
      const fallbackCode = Math.floor(100000 + random() * 900000).toString();
      authToken = null;
      fallbackVerificationCode = fallbackCode;
      verificationEmail = '';
      verificationMethod = 'local';

      try {
        const { data: result, parseError } = await client.startPasswordless(email);
        if (parseError) {
          logger.warn('passwordless/start returned a non-JSON response');
        }
        verificationEmail = email;
        verificationMethod = 'api';

        const raw =
          result.verification_code ||
          result.code ||
          result.verificationCode ||
          result.token ||
          result.otp;
        const apiCode = raw != null && raw !== '' ? String(raw).trim() : '';
        const isSixDigitOtp = /^\d{6}$/.test(apiCode);
        if (isSixDigitOtp) fallbackVerificationCode = apiCode;
        else if (apiCode) fallbackVerificationCode = '';
        return isSixDigitOtp ? apiCode : fallbackCode;
      } catch (_error) {
        logger.error('passwordless/start request failed');
        return fallbackCode;
      }
    }

    async function verifyCode(code, email) {
      try {
        const { data: result, ok, parseError, status } =
          await client.verifyPasswordless(code, email);
        if (parseError) {
          logger.warn('passwordless/token returned a non-JSON response');
        }
        if (!ok) {
          logger.warn('passwordless/token request failed', status);
          return { success: false, token: null };
        }

        const token =
          result.access_token ||
          result.token ||
          result.auth_token ||
          result.accessToken ||
          (result.data && (result.data.access_token || result.data.token));
        if (token) authToken = token;
        return { success: Boolean(token) || ok, token: token || null };
      } catch (_error) {
        logger.error('Verification request failed');
        return { success: false, token: null };
      }
    }

    function renderVerificationCode(options = {}) {
      if (options.savePreviousState !== false) navigation.saveState();
      const viewData = options.data || {};
      stateManager.setView(state.views.VERIFICATION_CODE);
      if (!chatBox) return false;

      chatBox.innerHTML = '';
      chatBox.appendChild(
        createElement(
          'div',
          'ai-message',
          "I've sent you a 6-digit verification code. Please enter it here:",
        ),
      );
      const codeContainer = createElement(
        'div',
        'ai-chat__verification-code-container',
      );
      const codeInput = createElement('input', 'ai-chat__verification-code-input');
      codeInput.type = 'text';
      codeInput.placeholder = '123456';
      codeInput.id = `${chatRoot.id}-verification-code-input`;
      const helperId = `${chatRoot.id}-verification-code-helper`;
      codeInput.setAttribute('aria-label', '6-digit verification code');
      codeInput.setAttribute('aria-describedby', helperId);
      codeInput.setAttribute('autocomplete', 'one-time-code');
      codeInput.dataset.aiChatVerificationCode = '';
      codeInput.maxLength = 6;
      codeInput.inputMode = 'numeric';
      codeInput.pattern = '[0-9]*';
      codeInput.value = String(viewData.code || '');
      codeContainer.appendChild(codeInput);
      const codeHelper = createElement(
        'div',
        'ai-chat__verification-code-helper',
        'Enter the 6-digit code we sent you',
      );
      codeHelper.id = helperId;
      codeContainer.appendChild(codeHelper);
      chatBox.appendChild(codeContainer);
      chatBox.scrollTop = chatBox.scrollHeight;
      if (options.focus !== false) focusLater(codeInput);

      if (continueButton) {
        continueButton.dataset.step = 'verification-code';
        setContinueDisabled(true);
        setContinueLabel('Verify Code', true);
      }
      const validateCodeInput = () => {
        setContinueDisabled(!core.isValidVerificationCode(codeInput.value.trim()));
      };
      codeInput.addEventListener('input', validateCodeInput);
      validateCodeInput();
      codeInput.addEventListener('keypress', (event) => {
        if (
          !/[0-9]/.test(event.key) &&
          event.key !== 'Backspace' &&
          event.key !== 'Delete'
        ) {
          event.preventDefault();
        }
      });
      return true;
    }

    function createMethodOption(method) {
      const option = createElement('button', 'ai-chat__verification-option');
      option.type = 'button';
      option.dataset.method = method;
      option.setAttribute('aria-pressed', 'false');
      option.innerHTML = verificationMethodMarkup[method];
      return option;
    }

    function renderVerificationMethod(name, options = {}) {
      if (options.savePreviousState !== false) navigation.saveState();
      const viewData = options.data || {};
      const restoredName = String(name || viewData.name || '');
      const selectedMethod = String(viewData.selectedMethod || '');
      stateManager.setView(state.views.VERIFICATION_METHOD);
      if (!chatBox) return false;

      chatBox.innerHTML = '';
      chatBox.appendChild(
        createElement(
          'div',
          'ai-message',
          `Hello ${restoredName}! Where should we send your verification code to get your quote?`,
        ),
      );
      const optionsContainer = createElement(
        'div',
        'ai-chat__verification-options',
      );
      optionsContainer.setAttribute('role', 'group');
      optionsContainer.setAttribute('aria-label', 'Verification method');
      optionsContainer.appendChild(createMethodOption('email'));
      optionsContainer.appendChild(createMethodOption('text'));
      chatBox.appendChild(optionsContainer);
      chatBox.scrollTop = chatBox.scrollHeight;
      chatBox.dataset.userName = restoredName;
      if (selectedMethod) chatBox.dataset.selectedMethod = selectedMethod;

      const methodOptions = optionsContainer.querySelectorAll(
        '.ai-chat__verification-option',
      );
      const selectMethod = (selectedOption) => {
        methodOptions.forEach((methodOption) => {
          const isSelected = methodOption === selectedOption;
          methodOption.classList.toggle('selected', isSelected);
          methodOption.setAttribute('aria-pressed', String(isSelected));
        });
      };
      methodOptions.forEach((option) => {
        if (selectedMethod && option.dataset.method === selectedMethod) {
          selectMethod(option);
        }
        option.addEventListener('click', () => {
          selectMethod(option);
          chatBox.dataset.selectedMethod = option.dataset.method;
          setContinueDisabled(false);
        });
      });

      if (continueButton) {
        continueButton.style.display = 'flex';
        continueButton.dataset.step = 'verification';
        setContinueDisabled(!selectedMethod);
      }
      return true;
    }

    async function handleApiVerification(codeInput, code, userName, email) {
      const targetEmail = verificationEmail || email;
      setContinueDisabled(true);
      setContinueLabel('Verifying...');
      const result = await verifyCode(code, targetEmail);
      if (!result.success || !email || !userName) {
        showInvalidInput(codeInput);
        resetVerificationButton();
        return;
      }

      setContinueDisabled(true);
      setContinueLabel('Creating quote...');
      const buyerPhone = chatBox.dataset.buyerPhone || '';
      const quoteAuthToken = result.token || authToken;
      try {
        const quoteData = await createQuote(
          userName,
          email,
          buyerPhone,
          quoteAuthToken,
        );
        chatBox.dataset.quoteId = quoteData.id;
        renderQuote(quoteData, userName, email);
        if (continueButton) {
          continueButton.style.display = 'none';
          continueButton.dataset.step = '';
          continueButton.textContent = 'Continue';
        }
        if (sendButton) sendButton.style.display = 'none';
        if (inputWrapper) inputWrapper.style.display = 'none';
      } catch (_error) {
        logger.error('Quote creation failed');
        showInvalidInput(codeInput);
        resetVerificationButton();
      }
    }

    function createVerificationMessage(code, userName, email, quoteSent) {
      if (quoteSent) {
        return `I've entered the verification code: ${code}. My name is ${userName}, email: ${email}. Quote has been sent to my email.`;
      }
      return `I've entered the verification code: ${code}. My name is ${userName}${
        email ? `, email: ${email}` : ''
      }.`;
    }

    async function handleLocalVerification(codeInput, code, userName, email) {
      if (!fallbackVerificationCode || code !== fallbackVerificationCode) {
        showInvalidInput(codeInput);
        return;
      }

      const quoteId = stockNumber || chatBox.dataset.quoteId || null;
      if (quoteId && email && userName) {
        setContinueDisabled(true);
        setContinueLabel('Sending quote...');
        try {
          await sendQuoteEmail(
            quoteId,
            userName,
            email,
            authToken,
          );
          sendMessage(createVerificationMessage(code, userName, email, true));
          restoreChatInput();
        } catch (_error) {
          sendMessage(createVerificationMessage(code, userName, email, false));
          resetVerificationButton();
        }
        return;
      }

      sendMessage(createVerificationMessage(code, userName, email, false));
      restoreChatInput();
    }

    async function handleVerificationCode() {
      if (!continueButton || continueButton.disabled || !chatBox) return;
      const codeInput = chatRoot.querySelector('[data-ai-chat-verification-code]');
      const code = codeInput ? codeInput.value.trim() : '';
      if (!core.isValidVerificationCode(code)) {
        if (codeInput) codeInput.focus();
        return;
      }

      const userName = chatBox.dataset.userName || '';
      const email = chatBox.dataset.userEmail || '';
      const targetEmail = verificationEmail || email;
      if (verificationMethod === 'api' && targetEmail) {
        await handleApiVerification(codeInput, code, userName, email);
        return;
      }
      await handleLocalVerification(codeInput, code, userName, email);
    }

    async function handleEmail() {
      if (!chatBox) return;
      const emailInput = chatRoot.querySelector('[data-ai-chat-email-address]');
      const email = emailInput ? emailInput.value.trim() : '';
      const userName = chatBox.dataset.userName || '';
      if (!email || !userName || !core.isValidEmail(email)) {
        if (emailInput) emailInput.focus();
        return;
      }

      chatBox.dataset.userEmail = email;
      setContinueDisabled(true);
      const originalHtml = continueButton ? continueButton.innerHTML : '';
      setContinueLabel('Sending...');
      try {
        await startVerification(email);
      } catch (_error) {
        if (continueButton) continueButton.innerHTML = originalHtml;
      }
      renderVerificationCode();
    }

    function handleVerificationMethod() {
      if (!continueButton || continueButton.disabled || !chatBox) return;
      const selectedMethod = chatBox.dataset.selectedMethod || '';
      const userName = chatBox.dataset.userName || '';
      if (!selectedMethod || !userName) return;

      if (selectedMethod === 'email') {
        renderEmailInput();
        return;
      }
      sendMessage(
        `I'd like to view my quote. My name is ${userName}. Please send verification code via text message.`,
      );
      restoreChatInput();
    }

    function handleName() {
      const pricingInput = chatRoot.querySelector('[data-ai-chat-pricing-name]');
      let name = pricingInput
        ? pricingInput.value.trim()
        : input
          ? input.value.trim()
          : '';
      if (!name && continueButton) name = continueButton.dataset.name || '';
      if (name) renderVerificationMethod(name);
    }

    function handleContinue(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!continueButton) return;

      switch (continueButton.dataset.step) {
        case 'verification-code':
          handleVerificationCode();
          break;
        case 'email':
          handleEmail();
          break;
        case 'verification':
          handleVerificationMethod();
          break;
        default:
          handleName();
      }
    }

    function captureViewData(view) {
      switch (view) {
        case state.views.PRICING_NAME: {
          const pricingInput = chatRoot.querySelector('[data-ai-chat-pricing-name]');
          return Object.freeze({
            name: pricingInput ? pricingInput.value : '',
            continueVisible: continueButton
              ? continueButton.style.display !== 'none'
              : false,
          });
        }
        case state.views.VERIFICATION_METHOD:
          return Object.freeze({
            name: chatBox ? chatBox.dataset.userName || '' : '',
            selectedMethod: chatBox ? chatBox.dataset.selectedMethod || '' : '',
          });
        case state.views.EMAIL_INPUT: {
          const emailInput = chatRoot.querySelector('[data-ai-chat-email-address]');
          return Object.freeze({ email: emailInput ? emailInput.value : '' });
        }
        case state.views.VERIFICATION_CODE: {
          const codeInput = chatRoot.querySelector('[data-ai-chat-verification-code]');
          return Object.freeze({ code: codeInput ? codeInput.value : '' });
        }
        default:
          return null;
      }
    }

    function restoreView(snapshot) {
      const restoreOptions = {
        data: snapshot.viewData || {},
        focus: false,
        savePreviousState: false,
      };
      switch (snapshot.view) {
        case state.views.PRICING_NAME:
          return renderPricingName(restoreOptions);
        case state.views.VERIFICATION_METHOD:
          return renderVerificationMethod(
            restoreOptions.data.name || '',
            restoreOptions,
          );
        case state.views.EMAIL_INPUT:
          return renderEmailInput(restoreOptions);
        case state.views.VERIFICATION_CODE:
          return renderVerificationCode(restoreOptions);
        default:
          return false;
      }
    }

    function resolveContinueDisabled(snapshot, fallback) {
      const viewData = snapshot.viewData || {};
      switch (snapshot.view) {
        case state.views.PRICING_NAME:
          return !String(viewData.name || '').trim();
        case state.views.VERIFICATION_METHOD:
          return !String(viewData.selectedMethod || '');
        case state.views.EMAIL_INPUT:
          return !core.isValidEmail(viewData.email || '');
        case state.views.VERIFICATION_CODE:
          return !core.isValidVerificationCode(viewData.code || '');
        default:
          return fallback;
      }
    }

    function bind() {
      if (isBound || !continueButton) return;
      continueButton.addEventListener('click', handleContinue);
      isBound = true;
    }

    function destroy() {
      if (!isBound || !continueButton) return;
      continueButton.removeEventListener('click', handleContinue);
      authToken = null;
      fallbackVerificationCode = '';
      verificationEmail = '';
      verificationMethod = '';
      isBound = false;
    }

    bind();

    return Object.freeze({
      bind,
      captureViewData,
      destroy,
      handleViewQuote,
      get isBound() {
        return isBound;
      },
      ownsView(view) {
        return pricingViews.has(view);
      },
      renderEmailInput,
      renderPricingName,
      renderVerificationCode,
      renderVerificationMethod,
      resolveContinueDisabled,
      restoreView,
    });
  }

  return Object.freeze({
    createPricingFlowController,
  });
});
